import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import ScrollToTop from '@/components/base/ScrollToTop';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList, categories } from '@/mocks/mangaData';

function formatViews(views: number): string {
  if (views >= 10000) return (views / 10000).toFixed(1) + '万';
  return views.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const mangaId = parseInt(id || '0', 10);
  const manga = mangaList.find((m) => m.id === mangaId);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已下架</p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.id === manga.category);
  const relatedManga = mangaList
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 5);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryInfo?.name || '漫画', href: `/category/${manga.category}` },
    { label: manga.title },
  ];

  const articleSchema = {
    headline: manga.title,
    author: { '@type': 'Person' as const, name: manga.author },
    datePublished: '2025-01-15',
    dateModified: '2026-08-01',
    description: manga.description,
    url: window.location.origin + `/article/${manga.id}`,
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${manga.title} - 虫虫漫画`,
          description: manga.description,
          url: window.location.origin + `/article/${manga.id}`,
        }}
      />
      <SEOSchema type="Article" data={articleSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <article className="py-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 w-full md:w-[320px]">
                  <div className="rounded-lg overflow-hidden aspect-[5/7] md:sticky md:top-24">
                    <img
                      src={manga.cover}
                      alt={`${manga.title} - 虫虫漫画韩国漫画`}
                      title={`${manga.title} - 韩国漫画在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${
                        manga.status === '连载中'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      {manga.status}
                    </span>
                    <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      {categoryInfo?.name}
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    {manga.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-6">
                    作者：<strong>{manga.author}</strong>
                  </p>

                  <div className="flex flex-wrap items-center gap-5 mb-6 text-sm text-foreground-600">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                      <strong>{manga.rating}</strong>
                      <span className="text-foreground-400">评分</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-eye-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                      <strong>{formatViews(manga.views)}</strong>
                      <span className="text-foreground-400">阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                      <strong>{manga.chapters}话</strong>
                      <span className="text-foreground-400">章节</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {manga.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 mb-8">
                    <h2 className="text-sm font-heading font-bold text-foreground-800 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {manga.description}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h2 className="text-lg font-heading font-bold text-foreground-800">
                      章节列表
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-2">
                      {Array.from({ length: Math.min(manga.chapters, 30) }, (_, i) => (
                        <button
                          key={i}
                          className="flex items-center justify-between px-4 py-2.5 text-sm rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer border border-background-200/70 text-left"
                        >
                          <span className="text-foreground-700 whitespace-nowrap">
                            第{manga.chapters - i}话
                          </span>
                          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex gap-3">
                    <button className="flex-1 inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                      开始阅读第一话
                    </button>
                    <button className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-background-100 hover:bg-accent-100 text-foreground-500 hover:text-accent-600 transition-all duration-200 cursor-pointer border border-background-200/70">
                      <i className="ri-bookmark-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                    <button className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-all duration-200 cursor-pointer border border-background-200/70">
                      <i className="ri-share-forward-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {relatedManga.length > 0 && (
              <section className="py-10 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-800 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {relatedManga.map((m) => (
                    <MangaCard key={m.id} {...m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}